import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Users, Star, Flame, Trash2, Mail, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminChildren() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchChildren = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/children/all/list`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setChildren(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch children:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  const handleDeleteChild = async (childId) => {
    if (!window.confirm('Are you sure you want to delete this child and all their data? This action cannot be undone.')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/children/${childId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Child deleted successfully');
        fetchChildren();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete child');
      }
    } catch (error) {
      toast.error('Failed to delete child');
    }
  };

  const viewChildTasks = (childId) => {
    navigate(`/admin/child/${childId}/tasks`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#264653] font-outfit">All Children</h1>
          <p className="text-[#6B7280] mt-1">{children.length} registered children</p>
        </div>

        {/* Children List */}
        {children.length === 0 ? (
          <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
            <Users className="w-16 h-16 text-[#08513F]/30 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">No Children Yet</h2>
            <p className="text-[#6B7280]">Children will appear here once they're registered.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {children.map((child, index) => (
              <Card 
                key={child.child_id}
                className="bg-white rounded-2xl border-[#D4AF37]/20 shadow-md hover:shadow-lg transition-shadow animate-slide-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`child-card-${child.child_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-16 w-16 border-2 border-[#D4AF37]/30">
                      <AvatarImage src={child.profile_image} />
                      <AvatarFallback className={`text-xl font-bold ${
                        child.gender === 'Muslim'
                          ? 'bg-[#08513F] text-white'
                          : 'bg-[#E76F51] text-white'
                      }`}>
                        {child.first_name?.charAt(0)}{child.last_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-[#264653] font-outfit">
                          {child.first_name} {child.last_name}
                        </h3>
                        <Badge className={`rounded-full ${
                          child.gender === 'Muslim'
                            ? 'bg-[#08513F]/10 text-[#08513F]'
                            : 'bg-[#E76F51]/10 text-[#E76F51]'
                        }`}>
                          {child.gender === 'Muslim' ? 'Muslim' : 'Muslimah'}
                        </Badge>
                        <span className="text-sm text-[#6B7280]">Age {child.age}</span>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-[#6B7280]">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {child.parent_email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-[#D4AF37]" />
                          {child.total_points} points
                        </span>
                        <span className="flex items-center gap-1">
                          <Flame className="w-4 h-4 text-[#E76F51]" />
                          {child.current_streak} day streak
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewChildTasks(child.child_id)}
                        className="text-[#08513F] hover:text-[#064031] hover:bg-[#08513F]/5 rounded-xl border-[#08513F]/20"
                        data-testid={`view-tasks-${child.child_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Tasks
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDeleteChild(child.child_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl"
                        data-testid={`delete-child-${child.child_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
